import React, { useEffect } from 'react';
import TextInput from 'src/modules/shared/components/atoms/inputs/text-input';
import LabelTextArea from 'src/modules/shared/components/atoms/inputs/label-text-area';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import { BaseFieldProps } from 'src/modules/shared/types/json-schema.type';

/**
 * Props for StringField component
 */
export interface StringFieldProps extends BaseFieldProps {
  placeholder?: string;
  uiSchema?: Record<string, unknown>;
}

/**
 * String field component for JSON Schema form
 * Renders different input types based on schema properties
 */
export const StringField: React.FC<StringFieldProps> = ({
  id,
  schema,
  value,
  onChange,
  disabled = false,
  nullable = false,
  placeholder = '',
  uiSchema = {},
}) => {
  const isConstString = schema.const !== undefined;

  // Ensure const values are written into form data
  useEffect(() => {
    if (isConstString && schema.const !== undefined && value !== schema.const) {
      onChange(schema.const);
    }
  }, [isConstString, schema.const, value, onChange, id]);
  if (isConstString) {
    return (
      <TextInput
        value={schema.const as string}
        disabled={true}
        variant="neutral"
        className="bg-gray-100"
      />
    );
  }

  const isEnumString = schema.enum !== undefined;
  if (isEnumString) {
    const options = (schema.enum as string[]).map((option) => ({
      value: option,
      content: option,
    }));

    const nullOption = {
      value: '',
      // TODO: check when implementing i18n
      content: nullable
        ? 'Selecciona o deja vacío para anular'
        : 'Selecciona una opción',
    };
    const selectOptions = [nullOption, ...options];

    return (
      <Select
        id={id}
        value={(value as string) || ''}
        onChange={(e) => onChange(e.target.value || (nullable ? null : ''))}
        disabled={disabled || (uiSchema as Record<string, boolean>).disabled}
        options={selectOptions}
        variant="primary"
      />
    );
  }

  const isTextareaFormat =
    schema.format === 'textarea' ||
    (uiSchema as Record<string, string>).widget === 'textarea';
  if (isTextareaFormat) {
    return (
      <LabelTextArea
        label=""
        inputProps={{
          value: (value as string) || '',
          onChange: (e) => {
            const raw = e.target.value;
            if (raw === '') {
              onChange(nullable ? null : undefined);
              return;
            }
            onChange(raw);
          },
          placeholder:
            (uiSchema as Record<string, string>).placeholder || placeholder,
          disabled: disabled || (uiSchema as Record<string, boolean>).disabled,
          rows: 4,
        }}
      />
    );
  }

  // TODO: check when implementing i18n
  const placeholderText = nullable
    ? 'Ingresa un valor o deja vacío para anular'
    : (uiSchema as Record<string, string>)?.placeholder || placeholder;

  return (
    <TextInput
      id={id}
      value={(value as string) || ''}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === '') {
          onChange(nullable ? null : undefined);
          return;
        }
        onChange(raw);
      }}
      placeholder={placeholderText}
      disabled={disabled || (uiSchema as Record<string, boolean>)?.disabled}
      variant="primary"
    />
  );
};
